import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { 
  Star, X, Mail, MessageSquare, MessageCircle, 
  Sparkles, RefreshCw, Send 
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/**
 * Modal réutilisable pour demander un avis client
 * Utilisable depuis les archives (bons d'entrée, commandes) ou la section Avis
 */
export default function ReviewRequestModal({ 
  isOpen, 
  onClose, 
  client, 
  token,
  onSuccess 
}) {
  const [channels, setChannels] = useState({
    email: !!client?.email,
    sms: !!client?.phone,
    whatsapp: false
  });
  const [generatedMessage, setGeneratedMessage] = useState('');
  const [generatingMessage, setGeneratingMessage] = useState(false);
  const [sending, setSending] = useState(false);

  if (!isOpen || !client) return null;

  const generateMessage = async () => {
    setGeneratingMessage(true);
    try {
      const response = await axios.post(`${API}/api/reviews/generate-message`, {
        client_name: client.name || client.clientName,
        purchase_type: client.purchase_type || client.deviceType || client.productName || 'service',
        visit_date: client.date || client.createdAt?.slice(0, 10) || new Date().toISOString().slice(0, 10)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedMessage(response.data.message);
    } catch (error) {
      console.error('Erreur génération:', error);
      toast.error('Erreur lors de la génération du message');
    } finally {
      setGeneratingMessage(false);
    }
  };

  const sendReviewRequest = async () => {
    const selectedChannels = [];
    if (channels.email) selectedChannels.push('email');
    if (channels.sms) selectedChannels.push('sms');
    if (channels.whatsapp) selectedChannels.push('whatsapp');
    
    if (selectedChannels.length === 0) {
      toast.error('Sélectionnez au moins un canal');
      return;
    }
    
    setSending(true);
    try {
      const clientName = client.name || client.clientName || 'Client';
      const clientPhone = client.phone || client.clientPhone || null;
      const clientEmail = client.email || client.clientEmail || null;
      
      const response = await axios.post(`${API}/api/reviews/send`, {
        client_name: clientName,
        client_phone: clientPhone,
        client_email: clientEmail,
        source: client.source || client.source_type || 'archive',
        source_id: client.source_id || client.id,
        purchase_type: client.purchase_type || client.deviceType || client.productName || 'service',
        visit_date: client.date || client.createdAt?.slice(0, 10) || new Date().toISOString().slice(0, 10),
        channels: selectedChannels,
        custom_message: generatedMessage || null,
        use_ai: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('⭐ Demande d\'avis envoyée !');
        onClose();
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error('Erreur envoi:', error);
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const clientName = client.name || client.clientName || 'Client';
  const clientPhone = client.phone || client.clientPhone;
  const clientEmail = client.email || client.clientEmail;
  const purchaseType = client.purchase_type || client.deviceType || client.productName || 'service';
  const visitDate = client.date || client.createdAt?.slice(0, 10) || '';

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400" />
              Demander un avis
            </h2>
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Info client */}
          <div className="bg-gray-700/50 rounded-lg p-4 mb-6">
            <div className="font-semibold text-white">{clientName}</div>
            <div className="text-sm text-gray-400 mt-1">
              {purchaseType} • {visitDate}
            </div>
            <div className="text-sm text-gray-400">
              {clientPhone && <span className="mr-3">{clientPhone}</span>}
              {clientEmail && <span>{clientEmail}</span>}
            </div>
          </div>

          {/* Canaux */}
          <div className="space-y-3 mb-6">
            <label className="text-sm font-medium text-white">Canaux d'envoi</label>
            
            {clientEmail && (
              <label className="flex items-center gap-3 p-3 rounded-lg border border-white/10 cursor-pointer hover:bg-white/5">
                <input
                  type="checkbox"
                  checked={channels.email}
                  onChange={(e) => setChannels({...channels, email: e.target.checked})}
                  className="w-4 h-4 rounded"
                />
                <Mail className="w-5 h-5 text-blue-400" />
                <span className="text-white">Email</span>
              </label>
            )}
            
            {clientPhone && (
              <>
                <label className="flex items-center gap-3 p-3 rounded-lg border border-white/10 cursor-pointer hover:bg-white/5">
                  <input
                    type="checkbox"
                    checked={channels.sms}
                    onChange={(e) => setChannels({...channels, sms: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <MessageSquare className="w-5 h-5 text-green-400" />
                  <span className="text-white">SMS</span>
                </label>
                
                <label className="flex items-center gap-3 p-3 rounded-lg border border-green-500/30 cursor-pointer hover:bg-green-500/10 bg-green-500/5">
                  <input
                    type="checkbox"
                    checked={channels.whatsapp}
                    onChange={(e) => setChannels({...channels, whatsapp: e.target.checked})}
                    className="w-4 h-4 rounded accent-green-500"
                  />
                  <MessageCircle className="w-5 h-5 text-green-500" />
                  <span className="text-green-400">WhatsApp</span>
                </label>
              </>
            )}
          </div>

          {/* Génération IA */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-white flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-400" />
                Message personnalisé par IA
              </label>
              <Button
                onClick={generateMessage}
                disabled={generatingMessage}
                size="sm"
                className="bg-purple-500 hover:bg-purple-600"
              >
                {generatingMessage ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-1" />
                    Générer
                  </>
                )}
              </Button>
            </div>
            
            <textarea
              value={generatedMessage}
              onChange={(e) => setGeneratedMessage(e.target.value)}
              placeholder="Cliquez sur 'Générer' pour créer un message personnalisé ou écrivez le vôtre..."
              className="w-full h-40 bg-white/10 border border-white/20 rounded-lg p-3 text-white text-sm resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 border-white/20 text-white hover:bg-white/10"
            >
              Annuler
            </Button>
            <Button
              onClick={sendReviewRequest}
              disabled={sending || (!channels.email && !channels.sms && !channels.whatsapp)}
              className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              {sending ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Envoyer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
